import React, { useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

type TooltipPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

interface HoverTooltipProps {
  /** The tooltip text/content shown on hover. */
  content: React.ReactNode;
  /** The element the tooltip is attached to (icon, badge, button, etc.). */
  children: React.ReactNode;
  /** Where the tooltip appears relative to the children. Defaults to "top". */
  position?: TooltipPosition;
  /** Text alignment inside the tooltip. Use "left" for rich/multi-item content. Defaults to "center". */
  align?: 'left' | 'center';
  /** Tailwind width class for the tooltip box. Defaults to fit-content with a max width. */
  width?: string;
  /** Extra classes applied to the wrapper element. */
  className?: string;
  /** Accessible label for the wrapper (falls back to the content when it's a string). */
  ariaLabel?: string;
  /** ARIA role for the wrapper (e.g. "img" for an icon-only trigger). */
  role?: string;
  /**
   * Whether the wrapper itself should be keyboard-focusable. Defaults to true so
   * non-interactive triggers (icons, badges, text) are reachable by keyboard. Set
   * to false when the child is already focusable (e.g. a button) to avoid a
   * duplicate tab stop — the tooltip still opens on the child's focus.
   */
  focusable?: boolean;
  /**
   * Render the tooltip in a portal to <body> with fixed positioning. Use when the
   * trigger lives inside an `overflow-hidden`/scrolling container that would clip
   * the default absolutely-positioned bubble.
   */
  portal?: boolean;
  /**
   * Hover-intent delay in ms before the tooltip shows (applies to focus too — menu
   * items auto-focus on open/arrow-key, which would pop the tooltip instantly).
   * Defaults to 0 (show immediately). Leaving/blurring cancels a pending show.
   */
  delayMs?: number;
  /** Optional test id for the wrapper. */
  dataTestId?: string;
}

const positionClasses: Record<TooltipPosition, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
  left: 'right-full top-1/2 -translate-y-1/2 mr-1',
  right: 'left-full top-1/2 -translate-y-1/2 ml-1',
  'top-left': 'bottom-full left-0 mb-1',
  'top-right': 'bottom-full right-0 mb-1',
  'bottom-left': 'top-full left-0 mt-1',
  'bottom-right': 'top-full right-0 mt-1',
};

/** Fixed-position equivalent of positionClasses, anchored to the trigger's viewport rect.
 *  Transforms shift by the tooltip's own size, so no measurement pass is needed. */
function fixedPositionStyle(
  rect: DOMRect,
  position: TooltipPosition,
): React.CSSProperties {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  switch (position) {
    case 'top':
      return {
        top: rect.top - 4,
        left: centerX,
        transform: 'translate(-50%, -100%)',
      };
    case 'bottom':
      return {
        top: rect.bottom + 4,
        left: centerX,
        transform: 'translate(-50%, 0)',
      };
    case 'left':
      return {
        top: centerY,
        left: rect.left - 4,
        transform: 'translate(-100%, -50%)',
      };
    case 'right':
      return {
        top: centerY,
        left: rect.right + 4,
        transform: 'translate(0, -50%)',
      };
    case 'top-left':
      return {
        top: rect.top - 4,
        left: rect.left,
        transform: 'translate(0, -100%)',
      };
    case 'top-right':
      return {
        top: rect.top - 4,
        left: rect.right,
        transform: 'translate(-100%, -100%)',
      };
    case 'bottom-left':
      return {
        top: rect.bottom + 4,
        left: rect.left,
        transform: 'translate(0, 0)',
      };
    case 'bottom-right':
      return {
        top: rect.bottom + 4,
        left: rect.right,
        transform: 'translate(-100%, 0)',
      };
  }
}

/**
 * Standard hover tooltip used across the portal. Follows the ARIA tooltip pattern:
 * shows on hover AND keyboard focus, is dismissible with Escape, uses the shared
 * white / border-ui-divider-lines / shadow style (no native `title` delay), and wires the
 * trigger to the tooltip via aria-describedby (cloned onto the child element so a
 * focused control is correctly associated). Wrap any element to give it a tooltip.
 */
export const HoverTooltip: React.FC<HoverTooltipProps> = ({
  content,
  children,
  position = 'top',
  align = 'center',
  width = 'w-max max-w-[220px]',
  className = '',
  ariaLabel,
  role,
  focusable = true,
  portal = false,
  delayMs = 0,
  dataTestId,
}) => {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [fixedPos, setFixedPos] = useState<React.CSSProperties | null>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearShowTimer = () => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  };
  useEffect(() => clearShowTimer, []);

  // A portaled tooltip is fixed at coordinates measured at show time; any scroll or
  // resize invalidates them (and mouseleave never fires under a wheel-scroll), so
  // dismiss rather than float at a stale position over unrelated content.
  useEffect(() => {
    if (!open || !portal) return;
    const dismiss = () => setOpen(false);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [open, portal]);

  // The trigger rect is measured at fire time (not enter time) — with a delay the
  // page can scroll under a resting pointer, and a stale rect would misplace the box.
  const doShow = () => {
    if (portal && wrapperRef.current) {
      setFixedPos(
        fixedPositionStyle(
          wrapperRef.current.getBoundingClientRect(),
          position,
        ),
      );
    }
    setOpen(true);
  };

  const show = () => {
    if (!delayMs) {
      doShow();
      return;
    }
    clearShowTimer();
    showTimer.current = setTimeout(doShow, delayMs);
  };

  const hide = () => {
    clearShowTimer();
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') hide();
  };

  // Merge aria-describedby onto the actual child so a focused control (e.g. a
  // button) is associated with the tooltip, not just the wrapper.
  const trigger = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<any>, {
        'aria-describedby': [
          (children as React.ReactElement<any>).props['aria-describedby'],
          tooltipId,
        ]
          .filter(Boolean)
          .join(' '),
      })
    : children;

  const tooltipBox = (
    <div
      id={tooltipId}
      role="tooltip"
      // z-70: portaled tooltips must render above portaled menus/popovers (z-60) —
      // a tooltip describing a menu item would otherwise hide behind the menu itself.
      style={
        portal
          ? {
              position: 'fixed',
              zIndex: 70,
              ...(fixedPos ?? { top: -9999, left: -9999 }),
            }
          : undefined
      }
      className={`${open ? 'block' : 'hidden'} ${portal ? '' : `absolute ${positionClasses[position]} z-10`} ${width} ${align === 'left' ? 'text-left' : 'text-center'} pointer-events-none rounded border border-ui-divider-lines bg-white p-2 text-body-sm font-normal text-ui-text-primary-text shadow-lg`}
    >
      {content}
    </div>
  );

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex ${className}`}
      role={role}
      aria-label={
        ariaLabel ?? (typeof content === 'string' ? content : undefined)
      }
      aria-describedby={tooltipId}
      tabIndex={focusable ? 0 : undefined}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={handleKeyDown}
      data-testid={dataTestId}
    >
      {trigger}
      {portal ? ReactDOM.createPortal(tooltipBox, document.body) : tooltipBox}
    </span>
  );
};
